'use client';
import React, { forwardRef, JSX, useState } from 'react';
import { cn } from '@fshr/shared-react';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	wrapperClassName?: string;
	label?: string;
	labelStyle?: string;
	labelTextClassName?: string;
	errorMessage?: React.ReactNode;
	blacklistedCharacters?: string;
	icon?: JSX.Element;
	rows?: number;
}

const Input = forwardRef(
	(
		{
			wrapperClassName,
			className,
			readOnly,
			label,
			labelStyle,
			id,
			name,
			errorMessage,
			required,
			blacklistedCharacters,
			icon,
			rows,
			labelTextClassName,
			maxLength,
			...props
		}: TextAreaProps,
		ref: React.Ref<HTMLTextAreaElement>
	) => {
		const [usedLength, setUsedLength] = useState(0);
		if (blacklistedCharacters) {
			props.value = props.value
				?.toString()
				.replace(new RegExp(`[${blacklistedCharacters}]`, 'g'), '');
		}

		return (
			<div
				className={cn([
					'relative w-full',
					{
						'pt-200': label,
						'text-red-700': errorMessage,
						'pb-[20px] lg:pb-[28px]': !readOnly,
					},
					wrapperClassName,
				])}
			>
				<div className="relative">
					<textarea
						rows={rows}
						{...{
							...props,
							placeholder:
								props.placeholder && required ? `${props.placeholder}*` : props.placeholder,
						}}
						className={cn(
							[
								'peer',
								'mt-[2px]',
								'pt-[12px]',
								'border-black',
								'text-body2',
								'focus:outline-hidden',
								'w-full',
								'placeholder:transition-opacity',
								'placeholder:opacity-100!',
								'placeholder:duration-300!',
								'placeholder:text-black!',
								'focus:placeholder:opacity-0!',
							],
							{
								'border-b bg-surface-form px-4': !readOnly,
								'placeholder:text-red-700! border-red-700': errorMessage,
								'pb-[35px]': maxLength,
							},
							className
						)}
						ref={ref}
						id={id}
						name={name}
						readOnly={readOnly}
						aria-labelledby={label && `${id}-label`}
						aria-invalid={errorMessage ? true : undefined}
						aria-describedby={errorMessage && id ? `${id}-error` : undefined}
						maxLength={maxLength}
						onChange={e => {
							if (maxLength && e.target.value.length <= maxLength) {
								setUsedLength(e.target.value.length ?? 0);
							}
							props.onChange?.(e);
						}}
					/>
					{label && (
						<label
							className={cn(
								'text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-[18px] peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-0 peer-focus:opacity-100 peer-focus:-translate-y-[18px]',
								labelStyle
							)}
							htmlFor={id}
							id={id && `${id}-label`}
						>
							<span className={labelTextClassName}>{`${label}${required ? '*' : ''}`}</span>
						</label>
					)}

					{icon ? <div className="absolute top-[15.5px] right-[16px]">{icon}</div> : null}

					{maxLength ? (
						<div
							className={`absolute bottom-2 pl-[16px] right-[16px] pb-[10px] text-text-secondary`}
						>
							<span className="text-label">{`${usedLength} / ${maxLength}`}</span>
						</div>
					) : null}
				</div>
				{errorMessage && (
					<span
						id={id ? `${id}-error` : undefined}
						className="absolute lg:pt-[2px] text-caption text-red-700"
						role="alert"
					>
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default Input;
